export enum CustomFormat {
  ColumnName = 'ColumnName',
  Subdirectory = 'Subdirectory',
  WorkflowName = 'WorkflowName',
}

export const isCustomFormat = (value: any) => Object.values(CustomFormat).includes(value);

type CustomValidator = {
  validator: RegExp | ((value: string) => boolean);
  errorMsg: string;
};

export const CustomFormatValidators: Record<CustomFormat, CustomValidator[]> = {
  [CustomFormat.ColumnName]: [
    {
      validator: /^(?![0-9])/,
      errorMsg: "Column name can't start with a number",
    },
    {
      validator: (value: string) => value.length >= 1 && value.length < 64,
      errorMsg: 'Column name must be between 1 and 64 characters long',
    },
    {
      validator: /^[\p{Ll}0-9_]+$/u,
      errorMsg: 'Only lowercase letters, numbers, and underscores are allowed',
    },

    ...['select', 'insert', 'update', 'delete', 'create', 'alter', 'drop', 'truncate', 'table', 'column'].map(
      (forbidden) => ({
        validator: (value: string) => !value.toLowerCase().startsWith(forbidden),
        errorMsg: `Column name can't contain a word "${forbidden}"`,
        forbidden,
      }),
    ),
  ],
  [CustomFormat.Subdirectory]: [
    {
      validator: /^\/?[a-zA-Z0-9_-]+(\/[a-zA-Z0-9_-]+)*\/?$/,
      errorMsg:
        'Invalid subdirectory format. Use alphanumeric characters, underscores, and hyphens, separated by slashes.',
    },
  ],
  [CustomFormat.WorkflowName]: [
    {
      errorMsg: "Workflow name must not contain any of: '/' '%' '&' '>' '<'",
      validator: /^[^/%&><]+$/,
    },
  ],
};

const validate = (format: CustomFormat, value: string) => {
  const patterns = CustomFormatValidators[CustomFormat[format]];

  for (const { validator } of patterns) {
    if (typeof validator === 'function') {
      if (!validator(value)) {
        return false;
      }
    } else {
      if (!validator.test(value)) {
        return false;
      }
    }
  }

  return true;
};

export const getErrorMessage = (format: CustomFormat, value: string): string => {
  const patterns = CustomFormatValidators[CustomFormat[format]];

  for (const { validator, errorMsg } of patterns) {
    if (typeof validator === 'function') {
      if (!validator(value)) {
        return errorMsg;
      }
    } else {
      if (!validator.test(value)) {
        return errorMsg;
      }
    }
  }
  return '';
};

export const customFormatDefinitions = {
  [CustomFormat.ColumnName]: validate.bind(null, CustomFormat.ColumnName),
  [CustomFormat.Subdirectory]: validate.bind(null, CustomFormat.Subdirectory),
  [CustomFormat.WorkflowName]: validate.bind(null, CustomFormat.WorkflowName),
};
